import { useEffect, useState } from "react";
import { ArrowUpRight, X } from "lucide-react";
import api from "@/lib/api";

const STORAGE_KEY = "sb_float_cta_dismissed";

// Floating sticky affiliate CTA pill — bottom-right.
// - Appears after the user scrolls past `revealAfter` px (default 480).
// - Deep-links to the currently-active carousel banner.
// - Dismissible (persists per session).
// - Sits above the BeGambleAware banner.
export default function FloatingAffiliateCTA({
  activeBanner,
  revealAfter = 480,
  testId = "floating-affiliate-cta",
}) {
  const [scrolled, setScrolled] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setDismissed(sessionStorage.getItem(STORAGE_KEY) === "1");
    setMounted(true);
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > revealAfter);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [revealAfter]);

  if (!mounted || !activeBanner || dismissed) return null;

  const handleDismiss = (e) => {
    e.preventDefault();
    e.stopPropagation();
    sessionStorage.setItem(STORAGE_KEY, "1");
    setDismissed(true);
  };

  const visible = scrolled;

  return (
    <div
      data-testid={testId}
      className="fixed right-3 sm:right-5 z-40 transition-all duration-500 ease-out"
      style={{
        bottom: "52px", // sits above the 18+ BeGambleAware banner (~36px)
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(20px)",
        pointerEvents: visible ? "auto" : "none",
      }}
    >
      <a
        href={activeBanner.clickUrl}
        target="_blank"
        rel="noopener noreferrer sponsored"
        onClick={() => {
          if (activeBanner.id) {
            api
              .post(`/track/banner/${encodeURIComponent(activeBanner.id)}/click`, { source: "floating-cta" })
              .catch(() => {});
          }
        }}
        data-testid={`${testId}-link`}
        className="group relative flex items-center gap-2 pl-3 pr-9 py-2.5 rounded-full font-mono uppercase tracking-[0.14em] text-[11px] sm:text-xs font-bold shadow-2xl transition-transform duration-200 hover:scale-[1.04] active:scale-[0.98]"
        style={{
          background:
            "linear-gradient(135deg, var(--sb-accent, #00E676) 0%, #00B85C 100%)",
          color: "#031007",
          boxShadow:
            "0 10px 30px -8px rgba(0, 230, 118, 0.55), 0 4px 12px rgba(0,0,0,0.4)",
          border: "1px solid rgba(0, 255, 140, 0.4)",
        }}
      >
        {/* Pulse ring */}
        <span
          aria-hidden="true"
          className="absolute inset-0 rounded-full"
          style={{
            boxShadow: "0 0 0 0 rgba(0, 230, 118, 0.6)",
            animation: "sbPulse 2.2s ease-out infinite",
          }}
        />
        <span className="relative flex items-center gap-1.5">
          <span className="hidden sm:inline">CLAIM</span>
          <span>{activeBanner.ctaLabel || "Free Bet"}</span>
          <ArrowUpRight
            size={14}
            strokeWidth={2.5}
            className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
          />
        </span>

        {/* Dismiss */}
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss offer"
          data-testid={`${testId}-dismiss`}
          className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center justify-center w-6 h-6 rounded-full transition-colors"
          style={{
            background: "rgba(3, 16, 7, 0.18)",
          }}
        >
          <X size={12} strokeWidth={3} color="#031007" />
        </button>
      </a>

      <style>{`
        @keyframes sbPulse {
          0%   { box-shadow: 0 0 0 0 rgba(0, 230, 118, 0.55); }
          70%  { box-shadow: 0 0 0 14px rgba(0, 230, 118, 0); }
          100% { box-shadow: 0 0 0 0 rgba(0, 230, 118, 0); }
        }
      `}</style>
    </div>
  );
}
